let depth = 0;

/** True only during the synchronous turn of a click, submit, or Enter. */
export function isUserCommitTurn(): boolean {
  return depth > 0;
}

export function beginUserCommitTurn(): void {
  depth += 1;
  // Macrotask: mutation runners (TanStack Query) hop microtasks before mutationFn;
  // effects, debounce and autosave only run on later macrotasks.
  setTimeout(() => {
    depth -= 1;
  }, 0);
}

const COMMIT_CONTROL_SELECTOR =
  'button, [role="button"], input[type="submit"], input[type="button"], [data-user-commit]';

/** Single-line controls whose Enter triggers implicit form submission. */
const SINGLE_LINE_SUBMIT_INPUT_TYPES: ReadonlySet<string> = new Set([
  'text',
  'search',
  'tel',
  'url',
  'email',
  'password',
  'number',
  'date',
  'month',
  'week',
  'time',
  'datetime-local',
]);

const SUBMIT_BUTTON_SELECTOR = 'button:not([type]), button[type="submit"], input[type="submit"]';

function eventElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target;
  }
  if (target instanceof Node) {
    return target.parentElement;
  }
  return null;
}

function commitControl(target: EventTarget | null): Element | null {
  const element = eventElement(target);
  if (element === null) {
    return null;
  }
  return element.closest(COMMIT_CONTROL_SELECTOR);
}

function isSilentCommit(element: Element): boolean {
  return element.getAttribute('data-user-commit') === 'silent';
}

function armOnClick(event: MouseEvent): void {
  const control = commitControl(event.target);
  if (control === null || isSilentCommit(control)) {
    return;
  }
  beginUserCommitTurn();
}

function formSubmitsOnEnter(form: HTMLFormElement): boolean {
  const submitter = form.querySelector(SUBMIT_BUTTON_SELECTOR);
  if (submitter !== null) {
    return !isSilentCommit(submitter);
  }
  let singleLineInputs = 0;
  for (const input of form.querySelectorAll('input')) {
    if (SINGLE_LINE_SUBMIT_INPUT_TYPES.has(input.type)) {
      singleLineInputs += 1;
    }
  }
  return singleLineInputs <= 1;
}

function enterSubmitsForm(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) {
    return false;
  }
  if (target instanceof HTMLButtonElement) {
    return target.type === 'submit' && !isSilentCommit(target);
  }
  if (!(target instanceof HTMLInputElement)) {
    return false;
  }
  if (target.type === 'submit') {
    return !isSilentCommit(target);
  }
  if (!SINGLE_LINE_SUBMIT_INPUT_TYPES.has(target.type) || target.form === null) {
    return false;
  }
  return formSubmitsOnEnter(target.form);
}

function armOnEnter(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || !enterSubmitsForm(event.target)) {
    return;
  }
  beginUserCommitTurn();
}

function armOnSubmit(event: SubmitEvent): void {
  if (event.submitter !== null && isSilentCommit(event.submitter)) {
    return;
  }
  beginUserCommitTurn();
}

/** Confirmação de toast acompanha o gesto. Efeito de tela (effect, debounce) fica de fora. */
export function installUserCommitTurnListener(): () => void {
  document.addEventListener('click', armOnClick, true);
  document.addEventListener('submit', armOnSubmit, true);
  document.addEventListener('keydown', armOnEnter, true);
  return () => {
    document.removeEventListener('click', armOnClick, true);
    document.removeEventListener('submit', armOnSubmit, true);
    document.removeEventListener('keydown', armOnEnter, true);
  };
}
